import React, { useState, useEffect, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import Header from '~/pages/Manager/Header';
import {
  addEquipmentsGroup,
  getEquipmentsGroups,
  getEquipmentsGroupsTemplate,
  importEquipmentsGroups,
  resetFileUploadErrors,
  getEquipmentTypes,
  getTurnsGroups,
  resetEquipmentsGroups,
  setEquipmentsGroupsValues,
  updateEquipmentsGroups,
} from '~/store/manager/actions';
import styles from '../styles';
import PF2MDataTable from '~/components/PF2MDataTable';
import {
  validateIdEquipament,
  validateName,
  validateTurnGroupId,
  validateDescription,
  validateMobileWorkflowNumber,
} from './validate';

const EquipmentsGroups = ({
  classes,
  equipmentsGroups,
  equipmentTypes,
  turnsGroups,
  getEquipmentsGroups: getAll,
  getEquipmentsGroupsTemplate: getTemplateAction,
  importEquipmentsGroups: importAction,
  addEquipmentsGroup: addItem,
  getEquipmentTypes: loadEquipmentTypes,
  getTurnsGroups: loadTurnsGroups,
  resetEquipmentsGroups: reset,
  setEquipmentsGroupsValues: updateItem,
  updateEquipmentsGroups: update,
}) => {
  const dispatch = useDispatch();

  const [dataLoaded, setDataLoaded] = useState(false);
  const { t: translate } = useTranslation();
  const [dialogMessage, setDialogMessage] = useState('');
  const [showDialogImport, setShowDialogImport] = useState(false);
  const fileUploadErrors = useSelector(state => state.manager.fileUploadErrors);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState('');
  const fileUploadSuccess = useSelector(state => state.errors.fileUploadSuccess);
  const [isEditing, setIsEditing] = useState(false);

  const tableRef = useRef();


  useEffect(() => {
    async function fetchData() {
      await getAll(true);
      await loadEquipmentTypes();
      await loadTurnsGroups();
    }
    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, getAll]);

  const warningPopup = () => {
    const message = (
      <>
        {translate('validation:CompleteTheRegistration')}
        <ul style={styles.listStyle}>
          <li>{translate('common:Checklist')}</li>
          <li>{translate('common:Hourmeter')}</li>
          <li>
            {translate('common:AverageLoad')}
            {' '}
            (
            {translate('common:TypesCargo')}
            )
          </li>
          <li>
            {translate('common:DrillingInformation')}
            {' '}
            (
            {translate('common:DrillingEquipment')}
            )
          </li>
        </ul>
      </>
    );
    setSuccessDialogMessage(message);
    setShowSuccessDialog(true);
  };

  useEffect(() => {
    if (fileUploadErrors && fileUploadErrors.length > 0) {
      const message = fileUploadErrors.map((error) => {
        const [rowCol, description] = error.split('-');
        const [row, col] = rowCol.split(';');
        return `${translate('validation:Row')} ${row} ${translate('validation:Col')} ${col} - ${translate(`validation:${description}`)}`;
      });
      setDialogMessage(message.join('\r\n'));
      setShowDialogImport(true);
      warningPopup();
    } else if (fileUploadSuccess === `${translate('validation:SheetImportSuccess')}`) {
      warningPopup();
    }
  }, [fileUploadErrors, fileUploadSuccess]);

  const columns = [
    {
      title: translate('common:Type'),
      field: 'id_equipament',
      editable: (_, rowData) => rowData && rowData.update_status === 'I',
      lookup: equipmentTypes,
      required: true,
      validate: validateIdEquipament,
    },
    {
      title: translate('common:Name'),
      field: 'name',
      editable: 'always',
      // unique: true,
      required: true,
      validate: rowData => validateName(rowData, tableRef),
    },
    {
      title: translate('common:ShiftGroup'),
      field: 'turn_group_id',
      customType: 'filterLookup',
      editable: 'always',
      lookup: turnsGroups,
      required: true,
      validate: validateTurnGroupId,
    },
    {
      title: translate('common:Description'),
      field: 'description',
      editable: 'always',
      required: true,
      validate: validateDescription,
    },
    {
      title: translate('common:Workflow'),
      field: 'mobile_workflow_number',
      editable: 'always',
      required: true,
      validate: validateMobileWorkflowNumber,
    },
    {
      title: translate('common:STATUS'),
      field: 'active',
      editable: 'always',
      customType: 'boolean',
      lookupKey: 'value',
      lookup: [
        { name: translate('common:Inactive'), value: 0 },
        { name: translate('common:Active'), value: 1 },
      ],
      defaultFilter: isEditing ? undefined : ['1'],
      required: true,
    },
  ];

  return (
    <div className={classes.tabContainer}>
      <Header
        getTemplateAction={getTemplateAction}
        importAction={importAction}
      />
      <PF2MDataTable
        ref={tableRef}
        title={translate('common:EquipmentGroup')}
        options={{
          search: true,
          selection: true,
          filtering: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          hideDelete: true,
          editableRowValidation: rowData => (rowData.id < 0 && rowData.update_status !== 'I'),
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 350px)',
        }}
        columns={columns}
        data={equipmentsGroups}
        onAdd={newData => addItem(newData)}
        onChange={(newData) => {
          setIsEditing(true);
          newData.forEach((row) => {
            updateItem({
              ...row,
              name: row.name.trim(),
              description: row.description.trim(),
            });
          });
        }}
        onDiscard={reset}
        onApply={() => {
          const hasPendingChanges = equipmentsGroups.some(row => row.update_status);
          if (!hasPendingChanges) return;
          update(equipmentsGroups);
        }}
      />
      <PF2MAlertDialog
        title={translate('validation:ImportErrors')}
        description={dialogMessage}
        open={showDialogImport}
        onConfirm={() => {
          setShowDialogImport(false);
          dispatch(resetFileUploadErrors());
        }}
        onClose={() => setShowDialogImport(false)}
        hasCancel={false}
      />
      <PF2MAlertDialog
        title={translate('common:RegistrationSteps')}
        description={
          <div style={styles.descriptionStyle}>{successDialogMessage}</div>
        }
        open={showSuccessDialog}
        onConfirm={() => {
          setShowSuccessDialog(false);
        }}
        onClose={() => setShowSuccessDialog(false)}
        hasCancel={false}
        badge
      />
    </div>
  );
};

EquipmentsGroups.propTypes = {
  classes: PropTypes.object.isRequired,
  equipmentsGroups: PropTypes.array,
  getEquipmentsGroups: PropTypes.func,
  getEquipmentsGroupsTemplate: PropTypes.func,
  importEquipmentsGroups: PropTypes.func,
  addEquipmentsGroup: PropTypes.func,
  equipmentTypes: PropTypes.array,
  getEquipmentTypes: PropTypes.func,
  getTurnsGroups: PropTypes.func,
  turnsGroups: PropTypes.array,
  resetEquipmentsGroups: PropTypes.func,
  setEquipmentsGroupsValues: PropTypes.func,
  updateEquipmentsGroups: PropTypes.func,
};

EquipmentsGroups.defaultProps = {
  equipmentsGroups: [],
  getEquipmentsGroups: () => {},
  getEquipmentsGroupsTemplate: () => {},
  importEquipmentsGroups: () => {},
  addEquipmentsGroup: () => {},
  equipmentTypes: [],
  getEquipmentTypes: () => {},
  getTurnsGroups: () => {},
  turnsGroups: [],
  resetEquipmentsGroups: () => {},
  setEquipmentsGroupsValues: () => {},
  updateEquipmentsGroups: () => {},
};

const mapStateToProps = state => ({
  equipmentsGroups: state.manager.equipmentsGroups,
  equipmentTypes: state.manager.equipmentTypes,
  turnsGroups: state.manager.turnsGroups,
});

const mapDispatchToProps = {
  getEquipmentsGroups,
  getEquipmentsGroupsTemplate,
  importEquipmentsGroups,
  addEquipmentsGroup,
  getEquipmentTypes,
  getTurnsGroups,
  resetEquipmentsGroups,
  setEquipmentsGroupsValues,
  updateEquipmentsGroups,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(EquipmentsGroups));
